import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import moment from "moment";
import { DatePicker } from "@mantine/dates";
import {
  IconExclamationMark,
  IconArrowBack,
  IconCreditCard,
  IconChevronDown,
} from "@tabler/icons";

import _ from "lodash";
import { reloadFormWithGuard } from "../helpers/formFieldSave";
import {
  assertClientPaymentHasAssignments,
  clientPaymentApiError,
} from "../helpers/clientPaymentMatching";
import { useTranslation } from "react-i18next";
import RemoteSelect2 from "../components/remoteSelect2";

import {
  Switch,
  TextInput,
  Select,
  Textarea,
  NumberInput,
  Group,
  Text,
  Button,
  Loader,
  Badge,
} from "@mantine/core";
import FormConfirmation from "./formConfirmation";
import { showNotification } from "@mantine/notifications";
import { DATA_GENDERS, DATA_RELATIONSHIP } from "../data/options";
import { useFormRender } from "../hooks/useFormRender";

import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import LocalPageList from "./localPageList";
import FileList from "./fileList";
import ActionCellRender from "./actionCellRender";
import { useFormatter } from "../hooks/useFomatter";

import { useServerApi } from "../hooks/userServerApi";
// import ReactJson from "react-json-view";
import { useCellRender } from "../hooks/useCellRender";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import { useForceUpdate, randomId } from "@mantine/hooks";
import DataTable from "./dataTable";
import Order from "../pages/operation/order";
import AddClientPaymentButton from "./addClientPaymentButton";
import DummyWidget from "./dummyWidget";
import ServiceCheckList from "./serviceCheckList";
import { useAuthUser, useAuthHeader } from "react-auth-kit";
import DebitNotePaymentMatchingList from "./debitNotePaymentMatchingList";
import { getFileIcon, getFileIconColor, isImageFile } from "./fileList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Image, Tooltip, Menu, UnstyledButton, ActionIcon } from "@mantine/core";
import { DATA_CLIENT_PAYMENT_METHOD } from "../data/options";

import UseClientCreditButton from "./useClientCreditButton";

import ReactJson from "react-json-view";
import { openFileUrl } from "../utils/openReport";

export const ImageThumbList = ({ files, size = 20, max = 2 }) => {
  const imageFiles = files?.filter((file) => isImageFile(file.mimetype));
  const otherFiles = files?.filter((file) => !isImageFile(file.mimetype));
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
      {imageFiles?.slice(0, max)?.map((file, index) => (
        <Tooltip
          key={index}
          zIndex={1000}
          label={
            <Image
              src={file.url}
              alt={`${file.name}`}
              fit="contain"
              height={250}
            />
          }
          position="bottom"
          withArrow
        >
          <Image
            src={file.url}
            alt={`Thumbnail ${index + 1}`}
            width={size}
            height={size}
            fit="cover"
            onClick={() => {
              window.open(file.url, "_blank");
            }}
          />
        </Tooltip>
      ))}
      {otherFiles?.length > 0 &&
        otherFiles?.slice(0, max)?.map((file, index) => (
          <FontAwesomeIcon
            alt={`${file.name}`}
            icon={getFileIcon(file.mimetype)}
            color={getFileIconColor(file.mimetype)}
            size="xl"
            onClick={() => {
              window.open(file.url, "_blank");
            }}
          ></FontAwesomeIcon>
        ))}

      {files?.length > max && (
        <Text size="sm" color="dimmed">
          +{files.length - max} more
        </Text>
      )}
    </div>
  );
};

const ClientPaymentList = ({ form: mainForm, readonly = false, ...props }) => {
  const dnId = _.get(mainForm.values, "_id");
  const dn = mainForm.values;
  const payments = _.get(mainForm.values, "clientPayments");
  const [formatter] = useFormatter();

  const paymentStatus = _.get(mainForm.values, "paymentStatus");
  const PAYMENT_STATUS_TO_LOCK =
    process.env.REACT_APP_LOCK_DN_PAYMENT_STATUS?.split(",") ?? [];
  const allowToEdit = !PAYMENT_STATUS_TO_LOCK.includes(paymentStatus);

  const [rows, setRows] = useState(payments);
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [printing, setPrinting] = useState(false);
  const clientId = _.get(mainForm.values, "client._id");
  const useClientCreditButtonRef = useRef(null);
  const formActionRef = useRef(null);
  const auth = useAuthUser();
  const authHeader = useAuthHeader();

  const buildPaymentChanges = (cp) => [
    cp.code          && { fieldLabel: "Code",    oldValue: "", newValue: cp.code },
    cp.amount != null && { fieldLabel: "Amount",  oldValue: "", newValue: `HKD ${Number(cp.amount).toLocaleString("en-HK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
    cp.paymentMethod && { fieldLabel: "Method",  oldValue: "", newValue: cp.paymentMethod },
    cp.date          && { fieldLabel: "Date",    oldValue: "", newValue: moment(cp.date).format("DD MMM YYYY") },
    cp.remark        && { fieldLabel: "Remark",  oldValue: "", newValue: cp.remark },
  ].filter(Boolean);

  const getDNAssignmentAmount = (payment) => {
    try {
      const assignment = payment.debitNoteAssignments?.find((assignment) => {
        const assignmentDnId =
          assignment.debitNote?._id ?? assignment.debitNote;
        if (!assignmentDnId) return false;
        return String(assignmentDnId) === String(dnId);
      });
      if (!assignment) return payment.amount;
      return assignment.paymentAmount;
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    const sortedPayments = _.uniqBy([...(payments ?? [])], (p) => p._id).sort(
      (a, b) => {
        return moment(a.date).diff(moment(b.date));
      },
    );
    setRows(sortedPayments);
  }, [payments]);

  useEffect(() => {
    if (!dnId || (payments && payments.length > 0)) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientPayment/search`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": (authHeader() || "").replace(/^Bearer\s+/i, ""),
            },
            body: JSON.stringify({
              condition: {
                $or: [
                  { debitNotes: dnId },
                  { "debitNoteAssignments.debitNote": dnId },
                ],
              },
              limit: 50,
            }),
          },
        );
        const json = await result.json();
        if (cancelled || !json?.success) return;
        const fetched = _.uniqBy(json.data?.docs ?? [], (p) => p._id).sort(
          (a, b) => moment(a.date).diff(moment(b.date)),
        );
        if (fetched.length > 0) setRows(fetched);
      } catch (error) {
        console.log(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dnId, payments]);

  const tableSchema = {
    hidePagination: true,
    columns: [
      // {   field: '_id',       headerName:"ID"            },
      {
        field: "type",
        headerName: "Type",
        cellRender: (col, data) => data.type,
      },
      {
        field: "date",
        headerName: "Payment Date",
        cellRender: (col, data) => (
          <>{data.date ? moment(data.date).format("YYYY-MM-DD") : ""}</>
        ),
      },
      {
        field: "code",
        headerName: "Code",
        cellRender: (col, data) => (
          <>
            {(data.debitNotes?.length > 0 || data.code) && (
              <>
                <Text size="xs">{data?.code}</Text>
                {data.debitNotes?.length > 1 && (
                  <Badge color="green" size="xs" radius="0">
                    {formatter.currency(getDNAssignmentAmount(data))} ·{" "}
                    {data.debitNotes.length} DN on receipt
                  </Badge>
                )}
              </>
            )}
          </>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        cellRender: (col, data) => data.status,
      },
      {
        field: "matchedDate",
        headerName: "Match Date",
        cellRender: (col, data) => (
          <>
            {data.matchedDate
              ? moment(data.matchedDate).format("YYYY-MM-DD")
              : ""}
          </>
        ),
      },

      {
        field: "bankAccount",
        headerName: "Bank Account",
        cellRender: (col, data) => data.bankAccount?.shortName ?? "",
      },
      {
        field: "amount",
        headerName: "Amount",
        cellRender: (col, data) => (
          <>
            <Text size="xs">
              {formatter.currency(getDNAssignmentAmount(data))}
            </Text>
            {data?.adjustmentAmount !== 0 && (
              <Text
                size="xs"
                color={data.adjustmentAmount < 0 ? "red" : "green"}
              >
                ( {formatter.currency(data.adjustmentAmount)} )
              </Text>
            )}
          </>
        ),
      },
      {
        field: "files",
        headerName: "Receipts",
        cellRender: (col, data) => ImageThumbList({ files: data.files }),
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          // {
          //   name: "edit",
          //   icon: IconPencil,
          //   props: {
          //     //   color:"red"
          //   },
          // },
        ],
        actionMenu: [
          // {
          //   name: "printReceipt",
          //   label: "Print Receipt",
          //   props: {
          //     size: "xs",
          //   },
          // },
          {
            name: "printReceipt",
            label: "Print Receipt",
            props: {
              size: "xs",
              disabled: readonly || !allowToEdit,
            },
          },
          {
            name: "edit",
            label: (data) =>
              data.status === "MATCHED" ? "View" : "Edit",
            props: {
              size: "xs",
              disabled: (data) => readonly || (!allowToEdit && data.status !== "MATCHED"),
            },
          },
          {
            name: "delete",
            label: "Delete",
            props: {
              color: "red",
              size: "xs",
              disabled: (data) => readonly || !allowToEdit || (data.status === "MATCHED" && data.type !== "CREDIT"),
            },
          },
        ],
      },
    ],
  };

  // if (readonly) {
  //   //remove edit and delete
  //   let actionMenu = tableSchema.columns.find(
  //     (c) => c.field === "action"
  //   ).actionMenu;
  //   actionMenu = [];
  //   actionMenu.splice(0, 1);
  //   actionMenu.splice(0, 1);
  // }

  const saveClientPayment = useCallback(
    async (values, { closeForm = true } = {}) => {
      try {
        assertClientPaymentHasAssignments(values);
        const result = await api.ClientPayment.update(values._id, values);
        if (result) {
          showNotification({
            title: "Success",
            message: "Payment updated",
          });
          if (closeForm) formActionRef.current?.close();

          api.addActivityLog({
            entityId: dnId,
            title: `Payment Updated${values.code ? ` [${values.code}]` : ""}`,
            by: auth().id,
            changes: buildPaymentChanges(values),
          });

          reloadFormWithGuard(mainForm);
        }
        return result;
      } catch (error) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Error",
          message: clientPaymentApiError(error),
        });
        throw error;
      }
    },
    [api, auth, dnId, mainForm],
  );

  const handleReceiptAutoSave = useCallback(
    async (updatedFiles, form) => {
      if (!form?.values?._id) return;
      try {
        await saveClientPayment(
          { ...form.values, files: updatedFiles },
          { closeForm: false },
        );
      } catch (error) {
        // notification handled in saveClientPayment
      }
    },
    [saveClientPayment],
  );

  const formSchema = useMemo(
    () => ({
    name: "AddClientPaymentSchema",
    title: "Edit Client Payment",
    description: "Please follow the step to create a New Client Payment",

    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "1000px", //xs, sm, xl  ....100%
    },

    apiEntity: "clientPayment",
    initialValues: {},

    validate: {
      amount: (value, formValues) => {
        if (!value) return "Payment Amount is required";
        // if (value <= 0 && dn?.type !== "CREDIT")
        //   return "Payment Amount must be greater than 0";
        // if (dn?.type == "CREDIT" && value > 0)
        //   return "Client Credit Payment Amount must be less than 0";
        return null;
      },
      bankAccount: (value) => {
        if (!value) return "Bank Account is required";
        return null;
      },
      date: (value) => {
        if (!value) return "Payment Date is required";
        return null;
      },
      files: (value) => {
        if (!value) return "Receipt File is required";
        return null;
      },
    },
    showSaveBar: true,

    layout: {
      containers: [
        {
          key: "t1",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Payment Info",
          },
          tab: [
            {
              key: "t1",
              label: "Payment Info",
              //icon: IconSettings,
            },
            {
              key: "tab-multiple",
              label: "Multiple Debit Notes",
              // icon: IconSettings,
            },
          ],
        },
      ],

      fields: [
        //Payment Date
        {
          name: "date",
          component: DatePicker,
          parent: "t1",
          type: "datePicker",
          props: {
            label: "Payment Date",
            required: true,
            clearable: true,
            valueFormat: "YYYY-MM-DD",
            allowFreeInput: true,
          },
        },

        //Payment Amount
        {
          name: "amount",
          component: NumberInput,
          parent: "t1",
          props: {
            label: "Payment Amount",
            //parse as money
            parser: formatter.currencyParser,
            formatter: formatter.currencyFormat,
            precision: 2,
            onChange: (value) => {
              console.log("amount", value);
            },
          },
        },

        //adjustment type
        {
          name: "adjustmentType",
          component: Select,
          parent: "t1",
          props: {
            label: "Adjustment Type",
            data: [
              "none",
              "Bank Charge",
              "Payment Amount Adjustment",
              "Commission",
              "A/P - Referral fee",
              "Temporary Receipt for Pending Policy",
              "Other",
            ],
            searchable: true,
            clearable: true,
            defaultValue: "none",
          },
        },

        //adjustment amount if type is not none
        {
          name: "adjustmentAmount",
          component: NumberInput,
          parent: "t1",
          props: {
            label: "Adjustment Amount",
            parser: formatter.currencyParser,
            formatter: formatter.currencyFormat,
            precision: 2,
            defaultValue: 0,
          },
        },

        //Payment Method
        {
          name: "paymentMethod",
          component: Select,
          parent: "t1",
          props: {
            label: "Payment Method",
            data: DATA_CLIENT_PAYMENT_METHOD,
            searchable: true,
            clearable: true,
          },
        },

        //Cheque Number
        {
          name: "chequeNumber",
          component: TextInput,
          parent: "t1",
          visibleOnly: (values, accessRight) =>
            values.paymentMethod === "Cheque",
          props: {
            label: "Cheque Number",
          },
        },

        //Direct to Provider
        {
          name: "directToProvider",
          parent: "t1",
          component: Switch,
          props: {
            type: "checkbox",
            size: "md",
            label: "Direct to Provider ",
            mt: "md",
          },
        },

        //Bank Account
        {
          name: "bankAccount",
          component: RemoteSelect2,
          parent: "t1",
          visibleOnly: (values, accessRight) => !values.directToProvider,

          props: {
            label: "Bank Account",
            apiEntity: "bankAccount",
            required: true,
            placeholder: "Bank Account",
            valueField: "_id",
            labelField: "name",

            // allowClear: true,
            // clearable: true,
            labelRender: (data) => {
              //Get bank name from bankAccount.bank
              return ` ${data?.shortName ?? ""} : ${
                data?.accountNumber ?? ""
              } `;
            },
            multiple: false,
            searchFields: ["name", "code", "cname"],
            clearable: true,
            sort: { by: "bank", order: "asc" },
          },
        },

        {
          name: "matchedDate",
          parent: "t1",
          component: DatePicker,
          type: "datePicker",
          props: {
            label: "Match Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },

        //Receipt File
        {
          name: "files",
          component: FileList,
          parent: "t1",
          props: {
            title: "Receipt File",
            tableMarginBottom: 10,
            showNoFilesMessage: false,
            defaultDisplayMode: 1,
            onAutoSave: handleReceiptAutoSave,
          },
        },
        {
          name: "remark",
          parent: "t1",
          component: Textarea,
          props: {
            label: "Remark",
          },
        },
        // {
        //   name: "isMultiple",
        //   parent: "section-debit-note",
        //   component: Switch,
        //   props: {
        //     label: "Pay to multiple debit notes",
        //   },
        // },
        {
          name: "debitNotes",
          parent: "tab-multiple",
          component: DebitNotePaymentMatchingList,
          props: {
            label: "Debit Notes",
            clientId,
            mode: "edit",
          },
        },
      ],
    },
  }),
  [
    clientId,
    dnId,
    formatter.currencyFormat,
    formatter.currencyParser,
    handleReceiptAutoSave,
  ],
  );

  const handleActionBtnClick = ({ action, data, rowIndex }) => {
    // console.log("handleActionBtnClick", action, data, rowIndex);
    switch (action) {
      case "delete":
        return deletePayment(data);
      case "printReceipt":
        return printReceipt(data);
      case "edit":
        return edit(data);
    }
  };

  const onSubmit = async ({ values }) => {
    try {
      await saveClientPayment(values, { closeForm: true });
    } catch (error) {
      formActionRef.current?.close();
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Error",
        message: clientPaymentApiError(error),
      });
    }
  };

  const [renderForm, formAction, formStatus, formSetting] = useFormRender(
    formSchema,
    null,
    onSubmit,
  );
  formActionRef.current = formAction;

  const deletePayment = async (payment) => {
    try {
      if (
        !window.confirm("Are you sure to delete this payment? " + payment.code)
      ) {
        return;
      }
      if (
        payment.status !== "MATCHING" &&
        payment.status !== "DIRECT" &&
        payment.type !== "CREDIT" &&
        payment.paymentMethod !== "CLIENT_CREDIT"
      ) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Error",
          message: "Only matching payment can be deleted: " + payment.type,
        });
        return;
      }
      const result = await api.ClientPayment.remove(payment._id);
      if (result) {
        showNotification({
          title: "Success",
          message: "Payment deleted",
        });

        api.addActivityLog({
          entityId: dnId,
          title: `Payment Deleted${payment.code ? ` [${payment.code}]` : ""}`,
          by: auth().id,
          changes: buildPaymentChanges(payment),
        });

        reloadFormWithGuard(mainForm);
      }
      //reload the
      if (useClientCreditButtonRef.current) {
        useClientCreditButtonRef.current.fetchCredit();
      }
    } catch (error) {
      console.log("deletePayment", error);
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Error",
        message: clientPaymentApiError(error),
      });
    }
  };

  const printReceipt = async (payment) => {
    try {
      // console.log("printReceipt > payment", payment);
      setPrinting(true);
      const result = await api.ClientPayment.printReceipt(payment._id);
      if (result) {
        showNotification({
          title: "Success",
          message: "Receipt printed",
        });
      }
      console.log("printReceipt > result", result);
      await openFileUrl(result.url);
      reloadFormWithGuard(mainForm);
      setPrinting(false);
    } catch (error) {
      console.log("printReceipt", error);
    }
  };

  const onUseCredit = (payment) => {
    console.log("onUseCredit", payment);
  };

  const edit = (payment) => {
    if (payment.status === "MATCHED") {
      formAction.open({ mode: "show", id: payment._id, data: payment });
      return;
    }
    formAction.open({ mode: "edit", id: payment._id, data: payment });
  };

  const onAdded = (payment) => {
    // console.log("OnAdded", payment);
    reloadFormWithGuard(mainForm);
  };

  const onRefund = (payment) => {
    // console.log("onRefund", payment);
    reloadFormWithGuard(mainForm);
  };

  const { t } = useTranslation();

  const handleRefundExcess = async () => {
    try {
      const debitNoteId = mainForm?.values?._id;
      if (!debitNoteId) return;
      if (!window.confirm("Are you sure to refund the excess payment?")) return;
      const data = await api.DebitNote.refundExcess(debitNoteId);
      onRefund(data);
    } catch (e) {
      console.log(e);
    }
  };

  const handleTransferToCredit = async () => {
    try {
      const debitNoteId = mainForm?.values?._id;
      if (!debitNoteId) return;
      if (
        !window.confirm(
          "Are you sure to transfer the excess payment to client credit?"
        )
      )
        return;
      const data = await api.DebitNote.transferExcessToCredit(debitNoteId);
      showNotification({
        title: t("Success"),
        message: t("Excess payment transferred to client credit"),
      });
      onRefund(data);
    } catch (e) {
      console.log(e);
      showNotification({
        title: t("Error"),
        color: "red",
        message: t("Failed to transfer excess to client credit"),
      });
    }
  };

  const showRefundButton =
    dn.type !== "CREDIT" && dn?.payment?.amount > dn?.clientPayable;
  const showClientCreditButton =
    dn?.type !== "CREDIT" &&
    dn?.clientPayable > 0 &&
    (dn?.payment?.amount ?? 0) - dn?.clientPayable < 0.01;

  return (
    <>
      {/* <ReactJson src={rows} style={{ backgroundColor: "white" }} collapsed /> */}
      {!_.isEmpty(rows) && (
        <DataTable
          data={rows ?? []}
          columns={tableSchema.columns}
          hidePagination={true}
          hideSearchBar={true}
          // onRowDoubleClick={handleOnEdit}
          onActionBtnClick={handleActionBtnClick}
          readonly={readonly || !allowToEdit}
        />
      )}
      {!printing && !readonly && allowToEdit && (
        <Group>
          {!readonly && (
            <AddClientPaymentButton mainForm={mainForm} onAdded={onAdded} />
          )}
          {showClientCreditButton && !readonly && allowToEdit && (
            <UseClientCreditButton
              mainForm={mainForm}
              onUseCredit={onUseCredit}
              ref={useClientCreditButtonRef}
            />
          )}
          {showRefundButton && !readonly && allowToEdit && (
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <UnstyledButton>
                  <Group spacing="xs">
                    <ActionIcon>
                      <IconArrowBack size="1rem" />
                    </ActionIcon>
                    <Text size="xs">{t("Refund Excess")}</Text>
                    <IconChevronDown size="0.8rem" />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Tooltip
                  label={t(
                    "Create a refund request to return excess payment to client's bank"
                  )}
                  position="right"
                  withArrow
                >
                  <Menu.Item
                    icon={<IconArrowBack size="1rem" />}
                    onClick={handleRefundExcess}
                  >
                    {t("Refund Money")}
                  </Menu.Item>
                </Tooltip>
                <Tooltip
                  label={t(
                    "Transfer excess payment to client's credit balance for future use"
                  )}
                  position="right"
                  withArrow
                >
                  <Menu.Item
                    icon={<IconCreditCard size="1rem" />}
                    onClick={handleTransferToCredit}
                  >
                    {t("To Client Credit")}
                  </Menu.Item>
                </Tooltip>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      )}
      {printing && (
        <Group spacing={"xs"}>
          <Loader size={"xs"} /> <Text size="xs"> Printing</Text>
        </Group>
      )}
      {renderForm()}
    </>
  );
};

export default ClientPaymentList;
